import { useCallback, useEffect, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';

export const getReplyRequestFields = (target, conversationId) =>
  target?.replyTo?.messageId && target.conversationId === conversationId
    ? { replyToMessageId: target.replyTo.messageId }
    : {};

export function mergeMessageReply(messages, incoming) {
  const index = messages.findIndex(
    (message) =>
      String(message._id || message.messageId) === String(incoming._id),
  );
  if (index < 0) return [...messages, incoming];
  // A late reply event must not overwrite newer read status or reactions.
  return messages.map((message, i) =>
    i === index ? { ...message, replyTo: incoming.replyTo } : message,
  );
}

export default function useMessengerReply({ conversationId, token, textRef }) {
  const [selection, setSelection] = useState(null);
  const requestRef = useRef(0);
  const conversationRef = useRef(conversationId);
  conversationRef.current = conversationId;

  const cancelReply = useCallback(() => {
    requestRef.current += 1;
    setSelection(null);
    textRef.current?.focus();
  }, [textRef]);

  useEffect(() => {
    requestRef.current += 1;
    setSelection(null);
  }, [conversationId]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    [],
  );

  const selectReply = useCallback(
    async (message) => {
      const messageId = message?._id || message?.messageId;
      if (!conversationId || !messageId) return;
      const request = ++requestRef.current;
      setSelection({ conversationId, pending: true });
      textRef.current?.focus();
      try {
        const res = await axiosConn.get(`messages/${messageId}/reply-preview`, {
          params: { conversationId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (
          request !== requestRef.current ||
          conversationRef.current !== conversationId
        )
          return;
        const replyTo = res.data?.data?.data;
        if (String(replyTo?.messageId) !== String(messageId))
          throw new Error('Reply unavailable');
        setSelection({ conversationId, replyTo });
      } catch {
        if (
          request !== requestRef.current ||
          conversationRef.current !== conversationId
        )
          return;
        setSelection({
          conversationId,
          error:
            'Could not load this reply. Select the message again or cancel.',
        });
      }
    },
    [conversationId, textRef, token],
  );

  const completeReply = useCallback((sentSelection) => {
    setSelection((current) => (current === sentSelection ? null : current));
  }, []);

  const isReplyConversationCurrent = useCallback(
    (id) => conversationRef.current === id,
    [],
  );

  useEffect(() => {
    if (!selection) return undefined;
    const onEscape = (event) => {
      if (event.key === 'Escape' && event.target === textRef.current)
        cancelReply();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [cancelReply, selection, textRef]);

  return {
    replySelection:
      selection?.conversationId === conversationId ? selection : null,
    selectReply,
    cancelReply,
    completeReply,
    isReplyConversationCurrent,
  };
}
